/**
 * The www.bookeper.pl site chrome: header navigation, footer columns, the company line and the
 * cookie-banner copy. Two apps render it: apps/landing (Vite, pl/en/uk, relative SPA routes) and
 * apps/hub (Next, Polish-only, served at www.bookeper.pl/interpretacje, absolute URLs). Each app
 * keeps its own markup; the items, their order and their Polish wording live only here, so the
 * hub cannot drift from the landing.
 *
 * Labels: landing translates `labelKey` through its locale files; `label` is the Polish text the
 * hub renders. apps/landing/src/test/site-chrome.test.tsx pins `label === pl[labelKey]` and the
 * key in every locale, so the two spellings cannot diverge.
 */

export const SITE_ORIGIN = 'https://www.bookeper.pl';
export const APP_ORIGIN = 'https://app.bookeper.pl';

/** Where a chrome link points, independent of the app that renders it. */
export type SiteLinkTarget =
  /** A landing route, localized per locale (`/biura`, `/en/biura`); the campaign query rides along. */
  | { kind: 'page'; path: string }
  /** A landing legal document, localized (`/regulamin` → `/en/terms`); no campaign query. */
  | { kind: 'legal'; path: string }
  /** A section of the localized home page (`/#pricing`); the campaign query rides along. */
  | { kind: 'section'; hash: `#${string}` }
  /** A www path another app serves through a landing rewrite; `localized` adds `/en`, `/uk`. */
  | { kind: 'www'; path: string; localized: boolean }
  /** A path on app.bookeper.pl, localized like the app's own routes. */
  | { kind: 'app'; path: string }
  /** An absolute URL (another site, `mailto:`), used as is. */
  | { kind: 'external'; href: string; rel?: string };

export interface SiteText {
  /** Landing locale-file key (`apps/landing/src/i18n/locales/*.json`). */
  labelKey: string;
  /** The Polish text; equal to `pl[labelKey]`. */
  label: string;
}

export interface SiteLink extends SiteText {
  id: string;
  target: SiteLinkTarget;
}

export const COMPANY = {
  name: 'Moonshot Ventures Yaroslav Krempovych',
  tradingName: 'Bookeper AI',
  address: 'Wałowa 3 lok. 23, 00-202 Warszawa',
  nip: '5252838946',
  regon: '387264153',
  email: 'contact@bookeper.pl',
  // Google Calendar appointment page (Google Meet) — canonical booking link;
  // also reachable via bookeper-redirect.vercel.app/meet.
  meetingUrl: 'https://calendar.app.google/B13gdNiEyH2uAtoD8',
  website: 'https://bookeper.pl',
} as const;

/**
 * Radian (radian.tax) is Bookeper's brand for companies and accounting firms; radian.tax links
 * back to bookeper.pl for JDG. This is the one reverse link, tagged so the referral is countable.
 */
export const RADIAN_URL = 'https://radian.tax/?utm_source=bookeper&utm_medium=referral';

/** The KIS interpretation hub (apps/hub), Polish-only, served on www through a rewrite. */
export const INTERPRETACJE_PATH = '/interpretacje';

/** The www logo, served by apps/landing (`public/logo.svg`); the hub ships a byte-equal copy. */
export const SITE_LOGO = { src: '/logo.svg', alt: 'Bookeper AI' } as const;

const interpretacjeTarget: SiteLinkTarget = {
  kind: 'www',
  path: INTERPRETACJE_PATH,
  localized: false,
};

/** Header navigation, in display order. The audience doors (`page`) come first. */
export const SITE_NAV_LINKS: readonly SiteLink[] = [
  { id: 'jdg', labelKey: 'nav.forJdg', label: 'Dla JDG', target: { kind: 'page', path: '/jdg' } },
  {
    id: 'biura',
    labelKey: 'nav.forBiura',
    label: 'Dla biur',
    target: { kind: 'page', path: '/biura' },
  },
  {
    id: 'wdrozenia',
    labelKey: 'nav.wdrozenia',
    label: 'Wdrożenia',
    target: { kind: 'page', path: '/wdrozenia' },
  },
  {
    id: 'features',
    labelKey: 'nav.features',
    label: 'Funkcje',
    target: { kind: 'section', hash: '#features' },
  },
  {
    id: 'how-it-works',
    labelKey: 'nav.howItWorks',
    label: 'Jak to działa',
    target: { kind: 'section', hash: '#how-it-works' },
  },
  {
    id: 'pricing',
    labelKey: 'nav.pricing',
    label: 'Cennik',
    target: { kind: 'section', hash: '#pricing' },
  },
  {
    id: 'regulations',
    labelKey: 'nav.regulations',
    label: 'Przepisy',
    target: { kind: 'section', hash: '#przepisy' },
  },
  { id: 'faq', labelKey: 'nav.faq', label: 'FAQ', target: { kind: 'section', hash: '#faq' } },
  {
    id: 'blog',
    labelKey: 'footer.blog',
    label: 'Blog',
    target: { kind: 'www', path: '/blog', localized: true },
  },
  {
    id: 'interpretacje',
    labelKey: 'footer.interpretacje',
    label: 'Interpretacje',
    target: interpretacjeTarget,
  },
];

/** Header account texts (the sign-in link and the sign-up button). */
export const SITE_NAV_TEXT = {
  login: { labelKey: 'nav.login', label: 'Zaloguj się' },
  signup: { labelKey: 'nav.signup', label: 'Załóż konto' },
  signupFree: { labelKey: 'nav.signupFree', label: 'Załóż konto za darmo' },
} as const satisfies Record<string, SiteText>;

/** Footer "Produkt" column, in display order; the guides sub-list follows it. */
export const FOOTER_PRODUCT_LINKS: readonly SiteLink[] = [
  {
    id: 'features',
    labelKey: 'footer.features',
    label: 'Funkcje',
    target: { kind: 'section', hash: '#features' },
  },
  {
    id: 'how-it-works',
    labelKey: 'footer.howItWorks',
    label: 'Jak to działa',
    target: { kind: 'section', hash: '#how-it-works' },
  },
  {
    id: 'pricing',
    labelKey: 'footer.pricing',
    label: 'Cennik',
    target: { kind: 'section', hash: '#pricing' },
  },
  {
    id: 'biura',
    labelKey: 'footer.biura',
    label: 'Dla biur rachunkowych',
    target: { kind: 'page', path: '/biura' },
  },
  {
    id: 'radian',
    labelKey: 'footer.radian',
    label: 'Dla firm i spółek → radian.tax',
    target: { kind: 'external', href: RADIAN_URL, rel: 'noopener' },
  },
  {
    id: 'wdrozenia',
    labelKey: 'footer.wdrozenia',
    label: 'Wdrożenia',
    target: { kind: 'page', path: '/wdrozenia' },
  },
  {
    id: 'regulations',
    labelKey: 'footer.regulations',
    label: 'Przepisy',
    target: { kind: 'section', hash: '#przepisy' },
  },
  {
    id: 'faq',
    labelKey: 'footer.faq',
    label: 'FAQ',
    target: { kind: 'section', hash: '#faq' },
  },
  {
    id: 'blog',
    labelKey: 'footer.blog',
    label: 'Blog',
    target: { kind: 'www', path: '/blog', localized: true },
  },
  {
    id: 'interpretacje',
    labelKey: 'footer.interpretacje',
    label: 'Interpretacje',
    target: interpretacjeTarget,
  },
  {
    id: 'calculators',
    labelKey: 'footer.calculators',
    label: 'Kalkulatory',
    target: { kind: 'www', path: '/kalkulatory', localized: false },
  },
  {
    id: 'zawody',
    labelKey: 'footer.zawody',
    label: 'Zawody',
    target: { kind: 'www', path: '/zawody', localized: false },
  },
  {
    id: 'analyzer',
    labelKey: 'footer.analyzer',
    label: 'Analizator',
    target: { kind: 'www', path: '/analyzer', localized: false },
  },
];

/**
 * The guides (przewodniki) have no index route, so the footer links each one. Polish-only paths
 * in every locale. Mirrors `GUIDES` in apps/landing/src/content/przewodniki.ts (pinned by test).
 */
export const PRZEWODNIKI_PATH = '/przewodniki';
export const FOOTER_GUIDE_LINKS: readonly { slug: string; label: string }[] = [
  { slug: 'kpir-elektroniczna-2026', label: 'KPiR elektroniczna 2026' },
  { slug: 'kod-pkd-jak-wybrac', label: 'Kod PKD — jak wybrać' },
  { slug: 'ksef-dla-jdg', label: 'KSeF dla JDG' },
  { slug: 'ksiegowosc-online', label: 'Księgowość online' },
  { slug: 'e-doreczenia-jdg-2026', label: 'e-Doręczenia dla JDG' },
];

/** Footer "Informacje" column, in display order. */
export const FOOTER_INFO_LINKS: readonly SiteLink[] = [
  {
    id: 'terms',
    labelKey: 'footer.terms',
    label: 'Regulamin',
    target: { kind: 'legal', path: '/regulamin' },
  },
  {
    id: 'privacy',
    labelKey: 'footer.privacy',
    label: 'Polityka prywatności',
    target: { kind: 'legal', path: '/polityka-prywatnosci' },
  },
  {
    id: 'cookies',
    labelKey: 'footer.cookies',
    label: 'Polityka cookies',
    target: { kind: 'legal', path: '/cookies' },
  },
  {
    id: 'cookie-settings',
    labelKey: 'footer.cookieSettings',
    label: 'Ustawienia plików cookie',
    target: { kind: 'app', path: '/settings/privacy' },
  },
  {
    id: 'contact',
    labelKey: 'footer.contact',
    label: 'Kontakt',
    target: { kind: 'external', href: `mailto:${COMPANY.email}` },
  },
];

/** Footer headings and the company lines. */
export const SITE_FOOTER_TEXT = {
  brandDescription: {
    labelKey: 'footer.brandDescription',
    label: 'Księgowość AI z pełnym śladem źródeł — dla JDG, biur rachunkowych i wdrożeń na miarę.',
  },
  product: { labelKey: 'footer.product', label: 'Produkt' },
  guides: { labelKey: 'footer.przewodniki', label: 'Przewodniki' },
  info: { labelKey: 'footer.info', label: 'Informacje' },
  allRights: { labelKey: 'footer.allRights', label: 'Wszystkie prawa zastrzeżone.' },
  madeWith: { labelKey: 'footer.madeWith', label: 'Made with <3 in Poland' },
} as const satisfies Record<string, SiteText>;

export type SocialIcon = 'x' | 'linkedin' | 'github' | 'email';

export const SOCIAL_LINKS: readonly {
  key: string;
  href: string;
  label: string;
  icon: SocialIcon;
}[] = [
  { key: 'x', href: 'https://x.com/bookeper_ai', label: 'X (Twitter)', icon: 'x' },
  {
    key: 'linkedin',
    href: 'https://linkedin.com/company/bookeper',
    label: 'LinkedIn',
    icon: 'linkedin',
  },
  { key: 'github', href: 'https://github.com/bookeper', label: 'GitHub', icon: 'github' },
  { key: 'email', href: `mailto:${COMPANY.email}`, label: 'Email', icon: 'email' },
];

/**
 * lucide-react dropped brand glyphs: LinkedIn and GitHub are simple-icons paths (24×24 viewBox);
 * X is a letterform and email is lucide's Mail, drawn by each app.
 */
export const SOCIAL_ICON_PATHS = {
  linkedin:
    'M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 1 1 0-4.124 2.062 2.062 0 0 1 0 4.124zM7.119 20.452H3.555V9h3.564v11.452z',
  github:
    'M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12',
} as const;

export type ConsentLocale = 'pl' | 'en' | 'uk';

export interface ConsentBannerCopy {
  message: string;
  accept: string;
  decline: string;
  privacy: string;
}

/**
 * The cookie banner's copy. Both banners store the choice in the one `bookeper_consent` cookie
 * (`./consent`), so a choice made on any www page, the hub included, holds on every other.
 */
export const CONSENT_BANNER_COPY: Record<ConsentLocale, ConsentBannerCopy> = {
  pl: {
    message:
      'Używamy niezbędnych plików cookie. Za Twoją zgodą użyjemy też analitycznych, by sprawdzać, jak korzystasz z Bookeper. Zgodę możesz w każdej chwili wycofać.',
    accept: 'Akceptuj',
    decline: 'Tylko niezbędne',
    privacy: 'Polityka prywatności',
  },
  en: {
    message:
      'We use essential cookies. With your consent we will also use analytics cookies to see how you use Bookeper. You can withdraw your consent at any time.',
    accept: 'Accept',
    decline: 'Essential only',
    privacy: 'Privacy policy',
  },
  uk: {
    message:
      'Ми використовуємо необхідні файли cookie. За Вашою згодою ми також використаємо аналітичні, щоб бачити, як Ви користуєтеся Bookeper. Згоду можна в будь-який момент відкликати.',
    accept: 'Прийняти',
    decline: 'Лише необхідні',
    privacy: 'Політика конфіденційності',
  },
};

/**
 * The Polish, absolute URL of a chrome link, for an app outside the landing SPA (the hub).
 * `siteOrigin` is the www origin (a preview origin in previews).
 */
export function absoluteSiteHref(
  target: SiteLinkTarget,
  siteOrigin: string = SITE_ORIGIN,
  appOrigin: string = APP_ORIGIN
): string {
  switch (target.kind) {
    case 'page':
    case 'legal':
    case 'www':
      return `${siteOrigin}${target.path}`;
    case 'section':
      return `${siteOrigin}/${target.hash}`;
    case 'app':
      return `${appOrigin}${target.path}`;
    case 'external':
      return target.href;
  }
}
